import { showNotification } from "@mantine/notifications";
import { create } from "zustand";
import { getCurrentUser } from "@/services/auth";
import { captureException } from "@/services/log";

// Tipos para notas novas (NotaItem)
interface PendingNotaNova {
  type: "nova";
  nota: string;
  justificativa: string;
  fase: string;
  idResposta: string;
  respondente: string;
  idEmpresaNota: string;
  grupo: string;
}

// Tipos para edições de notas existentes (EditaNotaItem)
interface PendingNotaEdicao {
  type: "edicao";
  mediaId: string;
  mediaOriginal: TMedia;
  nota: string;
  justificativa: string;
}

export type PendingNota = PendingNotaNova | PendingNotaEdicao;

interface PendingNotasStore {
  // Map usando chave única: para novas = `${idResposta}-${fase}`, para edições = mediaId
  pendingNotas: Map<string, PendingNota>;
  addPendingNota: (key: string, nota: PendingNota) => void;
  removePendingNota: (key: string) => void;
  clearAll: () => void;
  getPendingCount: () => number;
  hasPendingChanges: () => boolean;
  saveToLocalStorage: () => void;
  loadFromLocalStorage: (userId: string) => void;
}

const STORAGE_KEY_PREFIX = "pending-notas";

const getStorageKey = (userId: string) => `${STORAGE_KEY_PREFIX}-${userId}`;

const usePendingNotasStore = create<PendingNotasStore>((set, get) => ({
  pendingNotas: new Map(),

  saveToLocalStorage: () => {
    try {
      const userId = getCurrentUser()?.uid;
      if (!userId) return;

      const pendingNotas = get().pendingNotas;
      const serialized = JSON.stringify([...pendingNotas.entries()]);
      localStorage.setItem(getStorageKey(userId), serialized);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "QuotaExceededError") {
          showNotification({
            title: "Espaço de armazenamento esgotado",
            message: "Não foi possível salvar suas alterações localmente. Salve sua avaliação o quanto antes.",
            color: "red",
            autoClose: false
          });
        } else if (error.name === "SecurityError") {
          showNotification({
            title: "Armazenamento local bloqueado",
            message:
              "Seu navegador bloqueou o armazenamento local. Suas alterações não serão preservadas após atualizar a página.",
            color: "orange",
            autoClose: 8000
          });
        } else {
          showNotification({
            title: "Erro ao salvar localmente",
            message: "Suas alterações podem não ser preservadas após atualizar a página.",
            color: "orange",
            autoClose: 5000
          });
        }
        captureException(error);
      }
    }
  },

  loadFromLocalStorage: (userId: string) => {
    try {
      const stored = localStorage.getItem(getStorageKey(userId));
      if (stored) {
        const entries = JSON.parse(stored) as Array<[string, PendingNota]>;
        const restoredMap = new Map(entries);
        set({ pendingNotas: restoredMap });
      }
    } catch (error) {
      if (error instanceof Error) {
        showNotification({
          title: "Erro ao restaurar notas pendentes",
          message: "Não foi possível recuperar suas alterações anteriores.",
          color: "orange",
          autoClose: 5000
        });
        captureException(error);
      }
    }
  },

  addPendingNota: (key: string, nota: PendingNota) => {
    set((state) => {
      const newMap = new Map(state.pendingNotas);
      newMap.set(key, nota);
      return { pendingNotas: newMap };
    });
    get().saveToLocalStorage();
  },

  removePendingNota: (key: string) => {
    set((state) => {
      const newMap = new Map(state.pendingNotas);
      newMap.delete(key);
      return { pendingNotas: newMap };
    });
    get().saveToLocalStorage();
  },

  clearAll: () => {
    set({ pendingNotas: new Map() });
    get().saveToLocalStorage();
  },

  getPendingCount: () => {
    return get().pendingNotas.size;
  },

  hasPendingChanges: () => {
    return get().pendingNotas.size > 0;
  }
}));

export default usePendingNotasStore;
